import {
  ManualProductType,
  ManualDepositAdjustedType,
  ManualEmployeeCompensationAdjustedType,
  ManualAlternativeInvestmentAdjustedType,
  ManualInvestmentPortfolioAdjustedType,
  ManualDisposableIncomeAdjustedType,
  ManualDisposableIncomeAnnualAdjustedType,
} from "../interfaces/DTO/manualMimshakData";

// ---- Legacy string enums (kept for handlerCodeOptions only) ----

export enum RealEstateType {
  RESIDENTIAL = "residential",
  INVESTMENT = "investment",
  ABROAD = "abroad",
}

export enum BankAccountType {
  SAVINGS = "savings",
  CHECKING = "checking",
  FOREIGN_CURRENCY = "foreign_currency",
}

/** @deprecated Legacy string values — used only as legacyStringValue in adjustedTypeOptions for backward compatibility with old records that stored these strings in handler_code. Do not use for new records. */
export enum AlternativeInvestmentType {
  CRYPTO = "crypto",
  COMMODITY = "commodity",
  PRIVATE_EQUITY = "private_equity",
  HEDGE_FUND = "hedge_fund",
  SOCIAL_LENDING = "social_lending",
  OTHER = "other",
}

export enum EmployeeCompensationType {
  RSU = "rsu",
  STOCK = "stock",
}

// ---- Sub-type option — no label, label comes from formatManualAdjustedType ----

export interface SubTypeOption {
  adjustedProductType: number;
  legacyStringValue?: string; // backward compat: old string stored in policy_number
}

// ---- Field config ----

export interface FieldConfig {
  handlerCodeLabel: string;
  handlerCodeOptions?: Array<{ value: string; label: string }>;
  policyNumberLabel: string;
  adjustedTypeOptions?: SubTypeOption[];
  adjustedTypeLabel?: string; // label shown above the adjustedType toggle buttons
  policyNumberAutoGenerated?: boolean; // when true, policy_number is auto-generated as "temp-<random>"
  fundsSumLabel: string;
  depositAmountLabel?: string;
  withdrawalAmountLabel?: string;
  actualiationDateLabel?: string; // when defined, show a date field for actualiation_date
  savingFeeLabel?: string; // when defined, show a saving fee percentage field
  handlerCodeFreeSolo?: boolean; // when true, handler code field allows free-text in addition to list options
  allowFamilySelection?: boolean; // when true, allow selecting "family" instead of individual contact
}

export const generateTempPolicyNumber = (): string =>
  "temp-" + Math.random().toString(36).substring(2, 9);

/** a temporary policy number (generateTempPolicyNumber - an asset saved without one) is not a real number */
export const isTempPolicyNumber = (policyNumber?: string | null): boolean =>
  !!policyNumber && policyNumber.toLowerCase().startsWith("temp");

/** the policy number to show - none for a temporary one (not shown on a task or to the client) */
export const displayPolicyNumber = (policyNumber?: string | null): string | null =>
  policyNumber && !isTempPolicyNumber(policyNumber) ? policyNumber : null;

// ---- Format function for adjusted_product_type (numeric sub-types) ----

export const formatManualAdjustedType = (adjustedProductType: number): string => {
  switch (adjustedProductType) {
    // Deposit
    case ManualDepositAdjustedType.DEPOSIT_MAKAM:      return "פקדון / פק\"מ";
    case ManualDepositAdjustedType.DEPOSIT_FOREIGN:    return "פקדון מט\"ח";
    case ManualDepositAdjustedType.STRUCTURED_DEPOSIT: return "פקדון מובנה";
    case ManualDepositAdjustedType.BANK_SAVINGS_PLAN:  return "תכנית חיסכון בנקאית";
    case ManualDepositAdjustedType.MONEY_MARKET_FUND:  return "קרן כספית";
    case ManualDepositAdjustedType.MAKAM:              return "מק\"מ";
    case ManualDepositAdjustedType.GOVT_BOND:          return "אג\"ח ממשלתי";
    case ManualDepositAdjustedType.CORPORATE_BOND:     return "אג\"ח קונצרני";
    case ManualDepositAdjustedType.OTHER:              return "אחר";
    // Alternative investments
    case ManualAlternativeInvestmentAdjustedType.CRYPTO:         return "קריפטו";
    case ManualAlternativeInvestmentAdjustedType.COMMODITY:      return "סחורות";
    case ManualAlternativeInvestmentAdjustedType.PRIVATE_EQUITY: return "השקעות פרטיות";
    case ManualAlternativeInvestmentAdjustedType.HEDGE_FUND:     return "קרנות גידור";
    case ManualAlternativeInvestmentAdjustedType.SOCIAL_LENDING: return "הלוואות חברתיות";
    case ManualAlternativeInvestmentAdjustedType.OTHER:          return "אחר";
    // Investment portfolio
    case ManualInvestmentPortfolioAdjustedType.SELF_MANAGED: return "בניהול עצמי";
    case ManualInvestmentPortfolioAdjustedType.MANAGED:      return "מנוהל";
    // Employee compensation
    case ManualEmployeeCompensationAdjustedType.RSU:   return "RSU";
    case ManualEmployeeCompensationAdjustedType.STOCK: return "מניות";
    case ManualEmployeeCompensationAdjustedType.OTHER: return "אחר";
    // Disposable income monthly
    case ManualDisposableIncomeAdjustedType.MONTHLY:   return "הכנסה פנויה חודשית";
    case ManualDisposableIncomeAdjustedType.GRANT:     return "מענק";
    // Disposable income annual
    case ManualDisposableIncomeAnnualAdjustedType.ANNUAL:       return "הכנסה פנויה שנתית";
    case ManualDisposableIncomeAnnualAdjustedType.ANNUAL_BONUS: return "בונוס שנתי";
    case ManualDisposableIncomeAnnualAdjustedType.GRANT:        return "מענק";
    default: return "אחר";
  }
};

export const MANUAL_PRODUCT_FIELD_CONFIG: Record<ManualProductType, FieldConfig> = {
  [ManualProductType.REAL_ESTATE]: {
    handlerCodeLabel: "סוג נדל\"ן",
    handlerCodeOptions: [
      { value: RealEstateType.RESIDENTIAL, label: "מגורים" },
      { value: RealEstateType.INVESTMENT, label: "נדל\"ן להשקעה" },
      { value: RealEstateType.ABROAD, label: "נדל\"ן בחו\"ל" },
    ],
    policyNumberLabel: "כתובת",
    fundsSumLabel: "שווי נכס",
    depositAmountLabel: "הכנסה משכירות חודשית",
    withdrawalAmountLabel: "משיכות",
    allowFamilySelection: true,
  },
  [ManualProductType.BANK_ACCOUNT]: {
    handlerCodeLabel: "בנק",
    policyNumberLabel: "מספר חשבון",
    policyNumberAutoGenerated: true,
    fundsSumLabel: "יתרה",
    depositAmountLabel: "הפקדות חודשיות",
    withdrawalAmountLabel: "משיכות חודשיות",
    allowFamilySelection: true,
  },
  [ManualProductType.BANK_ACCOUNT_FOREIGN]: {
    handlerCodeLabel: "בנק",
    policyNumberLabel: "מספר חשבון",
    policyNumberAutoGenerated: true,
    fundsSumLabel: "יתרה",
    depositAmountLabel: "הפקדות חודשיות",
    withdrawalAmountLabel: "משיכות חודשיות",
    allowFamilySelection: true,
  },
  [ManualProductType.DEPOSIT]: {
    handlerCodeLabel: "בנק",
    policyNumberLabel: "מספר פקדון",
    policyNumberAutoGenerated: true,
    adjustedTypeLabel: "סוג פקדון",
    adjustedTypeOptions: [
      { adjustedProductType: ManualDepositAdjustedType.DEPOSIT_MAKAM,      legacyStringValue: "deposit_makam" },
      { adjustedProductType: ManualDepositAdjustedType.DEPOSIT_FOREIGN,    legacyStringValue: "deposit_foreign" },
      { adjustedProductType: ManualDepositAdjustedType.STRUCTURED_DEPOSIT, legacyStringValue: "structured_deposit" },
      { adjustedProductType: ManualDepositAdjustedType.BANK_SAVINGS_PLAN,  legacyStringValue: "bank_savings_plan" },
      { adjustedProductType: ManualDepositAdjustedType.MONEY_MARKET_FUND,  legacyStringValue: "money_market_fund" },
      { adjustedProductType: ManualDepositAdjustedType.MAKAM,              legacyStringValue: "makam" },
      { adjustedProductType: ManualDepositAdjustedType.GOVT_BOND,          legacyStringValue: "govt_bond" },
      { adjustedProductType: ManualDepositAdjustedType.CORPORATE_BOND,     legacyStringValue: "corporate_bond" },
      { adjustedProductType: ManualDepositAdjustedType.OTHER },
      { adjustedProductType: ManualProductType.DEPOSIT },
    ],
    fundsSumLabel: "יתרה",
    depositAmountLabel: "הפקדות שוטפות",
    actualiationDateLabel: "תאריך פירעון",
    allowFamilySelection: true,
  },
  [ManualProductType.EMPLOYEE_COMPENSATION]: {
    handlerCodeLabel: "שם חברה",
    policyNumberLabel: "מזהה מענק",
    policyNumberAutoGenerated: true,
    adjustedTypeLabel: "סוג פיצויים / מענק",
    adjustedTypeOptions: [
      { adjustedProductType: ManualEmployeeCompensationAdjustedType.RSU,   legacyStringValue: "rsu" },
      { adjustedProductType: ManualEmployeeCompensationAdjustedType.STOCK, legacyStringValue: "stock" },
      { adjustedProductType: ManualEmployeeCompensationAdjustedType.OTHER },
    ],
    fundsSumLabel: "שווי מענקים",
    depositAmountLabel: "מענקים בתקופה",
    actualiationDateLabel: "תאריך הבשלה",
    allowFamilySelection: true,
  },
  [ManualProductType.MYBANK]: {
    handlerCodeLabel: "שם בנק",
    policyNumberLabel: "מספר חשבון הבנק שלי",
    policyNumberAutoGenerated: true,
    fundsSumLabel: "יתרה",
    depositAmountLabel: "הפקדות",
    withdrawalAmountLabel: "משיכות",
    allowFamilySelection: true,
  },
  [ManualProductType.ALTERNATIVE_INVESTMENTS]: {
    handlerCodeLabel: "שם / פירוט",
    handlerCodeFreeSolo: true,
    policyNumberLabel: "מזהה",
    policyNumberAutoGenerated: true,
    adjustedTypeLabel: "סוג השקעה",
    adjustedTypeOptions: [
      { adjustedProductType: ManualAlternativeInvestmentAdjustedType.CRYPTO,         legacyStringValue: AlternativeInvestmentType.CRYPTO },
      { adjustedProductType: ManualAlternativeInvestmentAdjustedType.COMMODITY,      legacyStringValue: AlternativeInvestmentType.COMMODITY },
      { adjustedProductType: ManualAlternativeInvestmentAdjustedType.PRIVATE_EQUITY, legacyStringValue: AlternativeInvestmentType.PRIVATE_EQUITY },
      { adjustedProductType: ManualAlternativeInvestmentAdjustedType.HEDGE_FUND,     legacyStringValue: AlternativeInvestmentType.HEDGE_FUND },
      { adjustedProductType: ManualAlternativeInvestmentAdjustedType.SOCIAL_LENDING, legacyStringValue: AlternativeInvestmentType.SOCIAL_LENDING },
      { adjustedProductType: ManualAlternativeInvestmentAdjustedType.OTHER,          legacyStringValue: AlternativeInvestmentType.OTHER },
    ],
    fundsSumLabel: "שווי נוכחי",
    depositAmountLabel: "השקעה ראשונית",
    withdrawalAmountLabel: "משיכות",
    allowFamilySelection: true,
  },
  [ManualProductType.INVESTMENT_PORTFOLIO]: {
    handlerCodeLabel: "בית השקעות / בנק",
    handlerCodeFreeSolo: true,
    policyNumberLabel: "מספר תיק",
    policyNumberAutoGenerated: true,
    adjustedTypeLabel: "סוג ניהול",
    fundsSumLabel: "שווי תיק",
    depositAmountLabel: "הפקדות",
    withdrawalAmountLabel: "משיכות",
    savingFeeLabel: "דמי ניהול מצבירה",
    allowFamilySelection: true,
    adjustedTypeOptions: [
      { adjustedProductType: ManualInvestmentPortfolioAdjustedType.SELF_MANAGED },
      { adjustedProductType: ManualInvestmentPortfolioAdjustedType.MANAGED },
    ],
  },
  [ManualProductType.PURE_SAVINGS_POLICY]: {
    handlerCodeLabel: "יצרן",
    policyNumberLabel: "מספר פוליסה",
    fundsSumLabel: "יתרה כוללת",
    depositAmountLabel: "הפקדות חודשיות",
    withdrawalAmountLabel: "משיכות חודשיות",
    savingFeeLabel: "דמי ניהול מצבירה",
  },
  [ManualProductType.LOAN]: {
    handlerCodeLabel: "מלווה / בנק",
    policyNumberLabel: "מזהה הלוואה",
    policyNumberAutoGenerated: true,
    fundsSumLabel: "יתרת הלוואה",
    withdrawalAmountLabel: "החזר חודשי",
    allowFamilySelection: true,
  },
  [ManualProductType.MORTGAGE]: {
    handlerCodeLabel: "בנק משכנתא",
    policyNumberLabel: "מספר משכנתא",
    policyNumberAutoGenerated: true,
    fundsSumLabel: "יתרת משכנתא",
    withdrawalAmountLabel: "החזר חודשי",
    allowFamilySelection: true,
  },
  [ManualProductType.DISPOSABLE_INCOME]: {
    handlerCodeLabel: "מקור הכנסה",
    policyNumberLabel: "מזהה",
    policyNumberAutoGenerated: true,
    adjustedTypeLabel: "סוג הכנסה",
    adjustedTypeOptions: [
      { adjustedProductType: ManualDisposableIncomeAdjustedType.MONTHLY, legacyStringValue: "monthly" },
      { adjustedProductType: ManualDisposableIncomeAdjustedType.GRANT,   legacyStringValue: "grant" },
      { adjustedProductType: ManualProductType.DISPOSABLE_INCOME },
    ],
    fundsSumLabel: "הכנסה פנויה חודשית",
    depositAmountLabel: "הכנסות חודשיות",
    withdrawalAmountLabel: "הוצאות חודשיות",
    allowFamilySelection: true,
  },
  [ManualProductType.DISPOSABLE_INCOME_ANNUAL]: {
    handlerCodeLabel: "מקור הכנסה",
    policyNumberLabel: "מזהה",
    policyNumberAutoGenerated: true,
    adjustedTypeLabel: "סוג הכנסה",
    adjustedTypeOptions: [
      { adjustedProductType: ManualDisposableIncomeAnnualAdjustedType.ANNUAL,       legacyStringValue: "annual" },
      { adjustedProductType: ManualDisposableIncomeAnnualAdjustedType.ANNUAL_BONUS, legacyStringValue: "annual_bonus" },
      { adjustedProductType: ManualDisposableIncomeAnnualAdjustedType.GRANT,        legacyStringValue: "grant" },
      { adjustedProductType: ManualProductType.DISPOSABLE_INCOME_ANNUAL },
    ],
    fundsSumLabel: "הכנסה פנויה שנתית",
    depositAmountLabel: "הכנסות שנתיות",
    withdrawalAmountLabel: "הוצאות שנתיות",
    allowFamilySelection: true,
  },
  [ManualProductType.OTHER]: {
    handlerCodeLabel: "סוג",
    policyNumberLabel: "מזהה",
    policyNumberAutoGenerated: true,
    fundsSumLabel: "שווי",
    depositAmountLabel: "הפקדות",
    withdrawalAmountLabel: "משיכות",
    allowFamilySelection: true,
  },
};

export const getFieldConfig = (productType: ManualProductType): FieldConfig =>
  MANUAL_PRODUCT_FIELD_CONFIG[productType];

// מחזיר label להצגה של adjusted_product_type.
// תומך ברשומות חדשות (לפי adjustedProductType נומרי) וישנות (לפי legacyStringValue ב-policy_number).
export const formatSubTypeDisplay = (
  adjustedProductType: number | null | undefined,
  productType: ManualProductType,
  policyNumber: string
): string => {
  const options = MANUAL_PRODUCT_FIELD_CONFIG[productType]?.adjustedTypeOptions;
  if (!options) return "";

  // רשומה חדשה — adjusted_product_type שונה מ-productType
  if (adjustedProductType != null && adjustedProductType !== (productType as number)) {
    const match = options.find((o) => o.adjustedProductType === adjustedProductType);
    if (match) return formatManualAdjustedType(match.adjustedProductType);
  }

  // backward compat — רשומה ישנה עם string ב-policy_number
  const legacy = options.find((o) => o.legacyStringValue === policyNumber);
  if (legacy) return formatManualAdjustedType(legacy.adjustedProductType);

  return "";
};

export const formatManualProductType = (productType: ManualProductType): string => {
  switch (productType) {
    case ManualProductType.PURE_SAVINGS_POLICY:
      return "פוליסת חיסכון";
    case ManualProductType.REAL_ESTATE:
      return "נדל\"ן";
    case ManualProductType.BANK_ACCOUNT:
      return "עו\"ש בנק";
    case ManualProductType.BANK_ACCOUNT_FOREIGN:
      return "עו\"ש מט\"ח";
    case ManualProductType.DEPOSIT:
      return "פקדונות";
    case ManualProductType.EMPLOYEE_COMPENSATION:
      return "מענקי מניות";
    case ManualProductType.ALTERNATIVE_INVESTMENTS:
      return "השקעות אלטרנטיביות";
    case ManualProductType.INVESTMENT_PORTFOLIO:
      return "תיק השקעות";
    case ManualProductType.LOAN:
      return "הלוואה";
    case ManualProductType.MORTGAGE:
      return "משכנתא";
    case ManualProductType.DISPOSABLE_INCOME:
      return "הכנסה פנויה חודשית";
    case ManualProductType.DISPOSABLE_INCOME_ANNUAL:
      return "הכנסה פנויה שנתית";
    case ManualProductType.OTHER:
      return "אחר";
    default:
      return "לא ידוע";
  }
};

export const getAllManualProductTypes = (): Array<{ value: ManualProductType; label: string }> => [
  { value: ManualProductType.PURE_SAVINGS_POLICY,      label: formatManualProductType(ManualProductType.PURE_SAVINGS_POLICY) },
  { value: ManualProductType.REAL_ESTATE,              label: formatManualProductType(ManualProductType.REAL_ESTATE) },
  { value: ManualProductType.BANK_ACCOUNT,             label: formatManualProductType(ManualProductType.BANK_ACCOUNT) },
  { value: ManualProductType.BANK_ACCOUNT_FOREIGN,     label: formatManualProductType(ManualProductType.BANK_ACCOUNT_FOREIGN) },
  { value: ManualProductType.DEPOSIT,                  label: formatManualProductType(ManualProductType.DEPOSIT) },
  { value: ManualProductType.EMPLOYEE_COMPENSATION,    label: formatManualProductType(ManualProductType.EMPLOYEE_COMPENSATION) },
  { value: ManualProductType.ALTERNATIVE_INVESTMENTS,  label: formatManualProductType(ManualProductType.ALTERNATIVE_INVESTMENTS) },
  { value: ManualProductType.INVESTMENT_PORTFOLIO,     label: formatManualProductType(ManualProductType.INVESTMENT_PORTFOLIO) },
  { value: ManualProductType.LOAN,                     label: formatManualProductType(ManualProductType.LOAN) },
  { value: ManualProductType.MORTGAGE,                 label: formatManualProductType(ManualProductType.MORTGAGE) },
  { value: ManualProductType.DISPOSABLE_INCOME,        label: formatManualProductType(ManualProductType.DISPOSABLE_INCOME) },
  { value: ManualProductType.DISPOSABLE_INCOME_ANNUAL, label: formatManualProductType(ManualProductType.DISPOSABLE_INCOME_ANNUAL) },
  { value: ManualProductType.OTHER,                    label: formatManualProductType(ManualProductType.OTHER) },
];
